// Percorso dentro le pagine: lo stesso tipo di lettura del funnel /verifica-copertura
// (passi → dove ci si ferma → cosa si tocca → dove si esce), ma per ogni pagina di
// atterraggio e senza toccare le pagine una per una. Lo avvia <PageTracker /> in root.
//
// Eventi GA4 (parametri da registrare come dimensioni personalizzate, ambito evento):
//   sezione_vista   sezione, ordine, secondi       una volta per sezione e per visita alla pagina
//   cta_click       cta, destinazione, sezione, tipo
//   pagina_uscita   ultima_sezione, sezioni_viste, profondita, secondi, click, destinazione
//                   (destinazione "fuori-sito" = ha chiuso, è tornato indietro o ha messo la
//                   pagina in background; altrimenti il percorso interno verso cui è andato)
//   form_iniziato   form, sezione                  primo campo toccato di un form con data-form
//   form_abbandono  form, ultimo_campo, campi_compilati, secondi, azione
//
// Nomi delle sezioni: data-sezione sul <section> (da preferire: stabile e leggibile nei
// report), altrimenti l'id, altrimenti la prima classe non generica. Un'area si esclude
// con data-track-off.
//
// Privacy: mai valori digitati. Dei click si manda solo il testo di link e bottoni FUORI
// dai form (dentro ci sono le vie e i civici delle tendine indirizzo), tranne il bottone
// di invio; dei form solo i nomi dei campi compilati.
//
// SSG-safe: nessun accesso a window fuori dalle funzioni.

import { trackEvent } from './analytics'

const CLASSI_GENERICHE = new Set(['section', 'alt', 'dark', 'container', 'wrap'])
const CLICCABILI = 'a[href], button, [role="button"], [role="tab"], summary'
const SEZIONI = '[data-sezione], section, header, footer'
const CAMPI = 'input, select, textarea'

/** Nome di una sezione per i report. */
export function nomeSezione(el: Element): string {
  const esplicito = el.getAttribute('data-sezione')
  if (esplicito) return esplicito
  if (el.id) return el.id
  const tag = el.tagName.toLowerCase()
  if (tag === 'header' || tag === 'footer') return tag
  for (const c of Array.from(el.classList)) {
    if (!CLASSI_GENERICHE.has(c) && !c.startsWith('seg-')) return c
  }
  return tag
}

/** Testo di un link o bottone, compatto e tagliato a 60 caratteri. */
export function etichettaCta(el: Element): string {
  const raw = el.getAttribute('data-cta') || el.getAttribute('aria-label') || (el as HTMLElement).innerText || el.textContent || ''
  return raw.replace(/\s+/g, ' ').trim().slice(0, 60)
}

/** Dove porta un link: percorso interno (senza query), oppure tel / mailto / whatsapp / host esterno. */
export function destinazioneLink(href: string | null, origin: string): string {
  if (!href) return ''
  if (href.startsWith('tel:')) return 'tel'
  if (href.startsWith('mailto:')) return 'mailto'
  if (href.startsWith('#')) return href.slice(0, 60)
  try {
    const u = new URL(href, origin)
    if (u.hostname === 'wa.me' || u.hostname.endsWith('whatsapp.com')) return 'whatsapp'
    if (u.origin === origin) return (u.pathname + u.hash).slice(0, 100)
    return u.hostname
  } catch {
    return ''
  }
}

/** Tipo di elemento cliccato: distingue le schede (tab) e gli espandi/chiudi (FAQ, dettagli). */
export function tipoCta(el: Element): string {
  if (el.getAttribute('role') === 'tab' || el.hasAttribute('aria-selected')) return 'tab'
  if (el.hasAttribute('aria-expanded') || el.tagName === 'SUMMARY') return 'espandi'
  return el.tagName === 'A' ? 'link' : 'bottone'
}

/**
 * Nomi (mai valori) dei campi compilati di un form, separati da virgola. Con `toccati` contano
 * solo i campi modificati dalla persona: radio e select hanno spesso una scelta già impostata.
 */
export function campiCompilati(form: HTMLFormElement, toccati?: Set<string>): string {
  const nomi = new Set<string>()
  for (const el of Array.from(form.querySelectorAll<HTMLInputElement>(CAMPI))) {
    const nome = nomeCampo(el)
    if (!nome || (toccati && !toccati.has(nome))) continue
    const pieno = el.type === 'checkbox' || el.type === 'radio' ? el.checked : el.value.trim() !== ''
    if (pieno) nomi.add(nome)
  }
  return Array.from(nomi).join(',').slice(0, 100)
}

function nomeCampo(el: Element): string {
  return (el.getAttribute('name') || el.id || '').replace(/^(cf|wm|mk|pt|fnl)-/, '')
}

interface StatoForm {
  el: HTMLFormElement
  nome: string
  inizio: number
  ultimoCampo: string
  toccati: Set<string>
  tentato: boolean
}

let invioSegnato = false

/** Da chiamare insieme a lead_inviato: il form della pagina è andato a buon fine, niente abbandono. */
export function segnaInvioForm(): void {
  invioSegnato = true
}

/** Avvia il tracciamento della pagina corrente; restituisce la funzione che lo ferma. */
export function avviaTracciamentoPagina(): () => void {
  const inizio = Date.now()
  const secondi = () => String(Math.round((Date.now() - inizio) / 1000))
  // Al cambio di rotta nella SPA (widget di copertura → funnel) l'uscita parte quando la URL è
  // già quella nuova: senza fissarla, GA4 attribuirebbe uscita e abbandono alla pagina d'arrivo.
  const pagina = { page_location: window.location.href, page_title: document.title }
  const viste = new Set<Element>()
  const osservate = new Set<Element>()
  let ordine = 0
  let ultima = ''
  let click = 0
  let profondita = 0
  let uscitaInviata = false
  let form: StatoForm | null = null
  let linkInterno: { dest: string; t: number } | null = null
  invioSegnato = false

  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting || viste.has(e.target)) continue
        viste.add(e.target)
        io.unobserve(e.target)
        ultima = nomeSezione(e.target)
        trackEvent('sezione_vista', { sezione: ultima, ordine: String(++ordine), secondi: secondi() })
      }
    },
    // Una sezione conta come vista quando il suo bordo alto supera la metà dello schermo:
    // vale anche per le sezioni più alte del viewport, che non sarebbero mai visibili al 50%.
    { rootMargin: '0px 0px -50% 0px' },
  )

  const osserva = () => {
    for (const el of Array.from(document.querySelectorAll(SEZIONI))) {
      if (osservate.has(el) || el.closest('[data-track-off]')) continue
      // Menu e barre fisse sono sempre a schermo: conterebbero come "viste" da tutti.
      const pos = getComputedStyle(el).position
      if (pos === 'sticky' || pos === 'fixed') { osservate.add(el); continue }
      // Sezione dentro un'altra sezione già tracciata: conta il contenitore, non i pezzi.
      const padre = el.parentElement?.closest(SEZIONI)
      if (padre && !el.hasAttribute('data-sezione')) continue
      osservate.add(el)
      io.observe(el)
    }
  }
  osserva()
  // Sezioni che compaiono dopo (esito copertura, cambio segmento, contenuti caricati).
  let pendente: number | undefined
  const mo = new MutationObserver(() => {
    if (pendente) return
    pendente = window.setTimeout(() => { pendente = undefined; osserva() }, 300)
  })
  mo.observe(document.body, { childList: true, subtree: true })

  const sezioneDi = (el: Element) => {
    const s = el.closest(SEZIONI)
    return s ? nomeSezione(s) : ''
  }

  const onClick = (e: MouseEvent) => {
    const el = (e.target as Element | null)?.closest?.(CLICCABILI)
    if (!el || el.closest('[data-track-off]')) return
    const inForm = el.closest('form')
    const isSubmit = el.tagName === 'BUTTON' && (el as HTMLButtonElement).type === 'submit'
    if (inForm && !isSubmit) return
    click++
    const destinazione = el.tagName === 'A' ? destinazioneLink(el.getAttribute('href'), window.location.origin) : ''
    if (destinazione.startsWith('/')) linkInterno = { dest: destinazione, t: Date.now() }
    trackEvent('cta_click', {
      cta: etichettaCta(el),
      destinazione,
      sezione: sezioneDi(el),
      tipo: tipoCta(el),
    })
  }

  const onFocusIn = (e: FocusEvent) => {
    const el = e.target as Element | null
    if (!el?.matches?.(CAMPI)) return
    const f = el.closest<HTMLFormElement>('form[data-form]')
    if (!f) return
    if (!form || form.el !== f) {
      form = { el: f, nome: f.getAttribute('data-form') ?? '', inizio: Date.now(), ultimoCampo: '', toccati: new Set(), tentato: false }
      trackEvent('form_iniziato', { form: form.nome, sezione: sezioneDi(f) })
    }
    form.ultimoCampo = nomeCampo(el)
  }

  const onInput = (e: Event) => {
    const el = e.target as Element | null
    if (form && el && form.el.contains(el)) form.toccati.add(nomeCampo(el))
  }

  const onSubmit = (e: SubmitEvent) => {
    if (form && e.target === form.el) form.tentato = true
  }

  const onScroll = () => {
    const h = document.documentElement.scrollHeight
    if (h <= 0) return
    const p = Math.min(100, Math.round(((window.scrollY + window.innerHeight) / h) * 10) * 10)
    if (p > profondita) profondita = p
  }
  onScroll()

  // Form lasciato a metà: all'uscita dalla pagina, verso il sito o fuori.
  const abbandonoForm = () => {
    if (!form || invioSegnato) return
    trackEvent('form_abbandono', {
      form: form.nome,
      ultimo_campo: form.ultimoCampo,
      campi_compilati: form.el.isConnected ? campiCompilati(form.el, form.toccati) : '',
      secondi: String(Math.round((Date.now() - form.inizio) / 1000)),
      azione: form.tentato ? 'dopo_invio_fallito' : 'compilazione',
      ...pagina,
      transport_type: 'beacon',
    })
    form = null
  }

  // Uscita dalla pagina: chiusura, cambio app o scheda, link che ricarica la pagina (molti
  // link del sito sono <a> semplici) o cambio di rotta nella SPA. Una volta sola: chi torna e
  // poi converte resta visibile da cta_click e lead_inviato. Beacon perché la pagina chiude.
  const esci = (verso?: string) => {
    if (uscitaInviata) return
    uscitaInviata = true
    const recente = linkInterno && Date.now() - linkInterno.t < 5000 ? linkInterno.dest : ''
    trackEvent('pagina_uscita', {
      ultima_sezione: ultima,
      sezioni_viste: String(viste.size),
      profondita: String(profondita),
      secondi: secondi(),
      click: String(click),
      destinazione: verso ?? (recente || 'fuori-sito'),
      ...pagina,
      transport_type: 'beacon',
    })
    abbandonoForm()
  }
  // Safari può lanciare pagehide con la pagina ancora "visible": i due eventi vanno tenuti separati.
  const onVisibility = () => { if (document.visibilityState === 'hidden') esci() }
  const onPageHide = () => esci()

  document.addEventListener('click', onClick, true)
  document.addEventListener('focusin', onFocusIn)
  document.addEventListener('input', onInput, true)
  document.addEventListener('submit', onSubmit, true)
  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('pagehide', onPageHide)
  window.addEventListener('scroll', onScroll, { passive: true })

  return () => {
    // Cambio di rotta nella SPA: quando gira il cleanup la URL è già quella nuova.
    esci(window.location.pathname)
    io.disconnect()
    mo.disconnect()
    if (pendente) window.clearTimeout(pendente)
    document.removeEventListener('click', onClick, true)
    document.removeEventListener('focusin', onFocusIn)
    document.removeEventListener('input', onInput, true)
    document.removeEventListener('submit', onSubmit, true)
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener('pagehide', onPageHide)
    window.removeEventListener('scroll', onScroll)
  }
}
